import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Typography, Radio, message } from 'antd';
import styled from 'styled-components';
import { HomePageHeader } from './HomePageHeader';
import { HeaderLinks } from '../shared/admin/HeaderLinks';
import { HomePageBody } from './HomePageBody';
import analytics, { EventType } from '../analytics';
import { useTranslation } from '../../i18n/useTranslation';
import { BreadcrumbContextType, DEFAULT_CONTEXT, useBreadcrumbContext } from '../search/context/BreadcrumbContext';
import { AppBreadcrumb } from '../search/AppBreadcrumbs';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { DashboardV2, DASHBOARD_EXPORT_FILTERS_ATTRIBUTE, DASHBOARD_EXPORT_ROOT_ID } from './DashboardV2';

type DashboardView = 'classic' | 'new';

const CLASSIC_VIEW: DashboardView = 'classic';

/**
 * The page content area (.ant-layout-content) has 24px padding, so rendering the spinner
 * inline leaves it inset from the very top. Pin it to the top edge of the viewport (full
 * width, above other content) so the export loading bar sits flush at the top.
 */
const TopSpinnerBar = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
`;

const ClassicContent = () => {
    return (
        <>
            <HomePageHeader />
            <HomePageBody />
        </>
    );
};

/**
 * Owns all context subscriptions (breadcrumbs, translations) and the analytics effect.
 * Isolated here so that context updates never propagate to DashboardV2 or ClassicContent.
 */
const HomePageControls = ({
    view,
    onViewChange,
}: {
    view: DashboardView;
    onViewChange: (v: DashboardView) => void;
}) => {
    const { t } = useTranslation();
    const { breadcrumbArray, setBreadcrumbArray }: BreadcrumbContextType = useBreadcrumbContext();

    const [isExportingPdf, setIsExportingPdf] = useState(false);

    useEffect(() => {
        analytics.event({ type: EventType.HomePageViewEvent });
        setBreadcrumbArray(DEFAULT_CONTEXT.breadcrumbArray);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExportPdf = useCallback(async () => {
        const root = document.getElementById(DASHBOARD_EXPORT_ROOT_ID);
        if (!root) {
            message.error(t('PDF_EXPORT_ERROR'));
            return;
        }

        setIsExportingPdf(true);
        try {
            // Loaded on demand so jspdf/html-to-image stay out of the app-wide header bundle.
            const { exportDashboardToPdf } = await import('./exportDashboardPdf');
            await exportDashboardToPdf({
                root,
                filtersSummary: root.getAttribute(DASHBOARD_EXPORT_FILTERS_ATTRIBUTE) || '',
                labels: {
                    reportTitle: t('Enterprise Data Catalog Dashboard'),
                    generatedOnLabel: t('Generated on'),
                    appliedFiltersLabel: t('Applied Filters'),
                    noFiltersLabel: t('All data (no filters applied)'),
                    pageLabel: t('Page'),
                    ofLabel: t('of'),
                    productName: t('Enterprise Data Catalog'),
                    fileBaseName: 'enterprise-data-catalog-dashboard',
                },
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to export dashboard PDF:', error);
            message.error(t('PDF_EXPORT_ERROR'));
        } finally {
            setIsExportingPdf(false);
        }
    }, [t]);

    return (
        <>
            {isExportingPdf && (
                <TopSpinnerBar>
                    <ComponentSpinner />
                </TopSpinnerBar>
            )}
            {breadcrumbArray.length > 0 && <AppBreadcrumb breadcrumbs={breadcrumbArray} />}
            <Row wrap={false} className="f-page-header">
                <Col xs={24} lg={8}>
                    <Typography.Title className="f-page-heading">{t('Enterprise Data Catalog')}</Typography.Title>
                </Col>
                <Col xs={24} lg={8} className="text-center">
                    <Radio.Group
                        className="f-btn-tabs mx-auto"
                        value={view}
                        onChange={(e) => onViewChange(e.target.value)}
                    >
                        <Radio.Button value="classic">{t('Classic')}</Radio.Button>
                        <Radio.Button value="new">{t('New Experience')}</Radio.Button>
                    </Radio.Group>
                </Col>
                <Col xs={24} lg={8}>
                    <HeaderLinks
                        isClassicView={view === CLASSIC_VIEW}
                        showExportPdf={view !== CLASSIC_VIEW}
                        isExportingPdf={isExportingPdf}
                        onExportPdf={handleExportPdf}
                    />
                </Col>
            </Row>
        </>
    );
};

export const HomePage = () => {
    const [view, setView] = useState<DashboardView>('new');

    const handleViewChange = useCallback((v: DashboardView) => {
        setView(v);
    }, []);

    return (
        <>
            <HomePageControls view={view} onViewChange={handleViewChange} />
            {view === CLASSIC_VIEW ? <ClassicContent /> : <DashboardV2 />}
        </>
    );
};
